import { useEffect, useMemo, useState } from "react";
import { Play, Pause, RotateCcw } from "lucide-react";

const STEPS = 160;

export function useTradeTimeline(trades) {
  const [t0, tN] = useMemo(() => {
    const ts = (trades || []).map(t => new Date(t.timestamp).getTime()).filter(Number.isFinite);
    return ts.length ? [Math.min(...ts), Math.max(...ts)] : [0, 0];
  }, [trades]);
  const [cutoff, setCutoff] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => { setCutoff(null); setPlaying(false); }, [t0, tN]);

  useEffect(() => {
    if (!playing) return;
    const step = Math.max(1, (tN - t0) / STEPS);
    const id = setInterval(() => {
      setCutoff(c => {
        const next = (c ?? t0) + step;
        if (next >= tN) { setPlaying(false); return null; }
        return next;
      });
    }, 70);
    return () => clearInterval(id);
  }, [playing, t0, tN]);

  const togglePlay = () => {
    if (!playing && cutoff == null) setCutoff(t0);
    setPlaying(p => !p);
  };
  const reset = () => { setPlaying(false); setCutoff(null); };
  return { t0, tN, cutoff, setCutoff, playing, setPlaying, togglePlay, reset, ready: tN > t0 };
}

const fmtDate = (ms) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const fmtDateTime = (ms) => new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function TimeSlider({ timeline }) {
  const { t0, tN, cutoff, setCutoff, playing, setPlaying, togglePlay, reset } = timeline;
  const value = cutoff ?? tN;
  const pct = ((value - t0) / (tN - t0)) * 100;
  const fmt = tN - t0 < 3 * 86400000 ? fmtDateTime : fmtDate;
  return (
    <div className="absolute bottom-3 left-3 right-3 z-10 flex items-center gap-3 px-3 py-2 bg-black/75 border border-white/15 backdrop-blur-md"
      data-testid="viz-time-slider">
      <button onClick={togglePlay} data-testid="viz-time-play" title={playing ? "Pause" : "Play timeline"}
        className="flex items-center justify-center w-8 h-8 shrink-0 bg-bt-gold text-black hover:bg-bt-gold/80 transition-colors">
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>
      <button onClick={reset} data-testid="viz-time-reset" title="Show all trades"
        className="flex items-center justify-center w-8 h-8 shrink-0 border border-white/20 text-white/70 hover:text-white transition-colors">
        <RotateCcw className="w-3.5 h-3.5" />
      </button>
      <span className="hidden sm:block text-[10px] font-mono text-white/50 shrink-0">{fmt(t0)}</span>
      <input type="range" min={t0} max={tN} step={Math.max(1, (tN - t0) / 1000)} value={value}
        onChange={(e) => { setPlaying(false); const v = Number(e.target.value); setCutoff(v >= tN ? null : v); }}
        data-testid="viz-time-range" className="flex-1 min-w-0 cursor-pointer accent-[#eab308]"
        style={{ background: `linear-gradient(to right, #eab308 ${pct}%, rgba(255,255,255,0.15) ${pct}%)`, height: 4 }} />
      <span className="hidden sm:block text-[10px] font-mono text-white/50 shrink-0">{fmt(tN)}</span>
      <span className="text-xs font-mono text-bt-gold shrink-0 min-w-[120px] text-right" data-testid="viz-time-label">
        {cutoff == null ? "All time" : fmt(cutoff)}
      </span>
    </div>
  );
}
